export const Stages = [
  { id: 'dev', name: 'Development' },
  { id: 'prod', name: 'Production' },
];

export class ResourceQuota {
  cpuLimit: number;
  cpuRequest: number;
  memoryLimit: number;
  memoryRequest: number;

  constructor() {
    this.cpuLimit = 4000;
    this.cpuRequest = 4000;
    this.memoryLimit = 8192;
    this.memoryRequest = 8192;
  }
}

export class ArgoProject {
  repositories: string[];
  rootPath: string;
  helmRepositories: string[];

  constructor() {
    this.repositories = [''];
    this.rootPath = '';
    this.helmRepositories = [];
  }
}

export class Tenant {
  id: string;
  stage: string;
  name: string;
  ownerObjectId: string;
  securityObjectIds: string[];
  pimEnabled: boolean;
  pimGroupId: string | null;
  applicationId: string | null;
  application: Application | null;
  slackChannel: string;
  tier: string;
  clusterId: string | null;
  nodeGroups: string[];
  namespaceQuota: number;
  resourceQuota: ResourceQuota;
  argocdProject: ArgoProject | null;
  argoUrl?: string;
  errors: Record<string, string | undefined | (string | undefined)[]>;

  constructor(props: { ownerObjectId: string }) {
    this.id = '';
    this.stage = 'dev';
    this.name = '';
    this.ownerObjectId = props.ownerObjectId;
    this.securityObjectIds = [];
    this.pimEnabled = false;
    this.pimGroupId = null;
    this.applicationId = null;
    this.application = null;
    this.slackChannel = '';
    this.tier = '';
    this.clusterId = null;
    this.nodeGroups = [];
    this.namespaceQuota = 3;
    this.resourceQuota = new ResourceQuota();
    this.argocdProject = null;
    this.errors = {};
  }

  getNamespaceQuota(): number {
    return this.namespaceQuota;
  }

  getClusterName(): string {
    return this.clusterId?.split('/')[1] ?? '';
  }

  getRegion(): string | undefined {
    return this.clusterId?.split('/')[1].split('-')[1];
  }

  getCpuRequest(): number {
    return this.resourceQuota.cpuRequest / 1000;
  }

  getCpuLimit(): number {
    return this.resourceQuota.cpuLimit / 1000;
  }

  getMemoryRequest(): number {
    return this.resourceQuota.memoryRequest / 1024;
  }

  getMemoryLimit(): number {
    return this.resourceQuota.memoryLimit / 1024;
  }
}

export type UpdateTenant = Omit<Tenant, 'ownerObjectId'>;

export type OwnerObject = {
  id: string;
  name: string;
};

export type SecurityObject = {
  id: string;
  name: string;
};

export type Application = {
  id: string;
  name: string;
  ccname: string;
  ouname: string;
  buname: string;
};

export type Cluster = {
  id: string;
  name: string;
  tier: string;
  stage: string;
  nodeGroups: string[];
};

export type TenantFilter = {
  type: string;
  value: string;
  matches: (value: string, tenant: Tenant) => boolean;
};

export const FILTER_ALL = 'all';
export const FILTER_ALL_LABEL = 'All';

export type TenantSpec = {
  stage: string;
  tier: string;
  id: string;
  name: string;
  ownerObjectId: string;
  securityObjectIds: string[];
  cabInform: boolean;
  applicationId: string;
  slackChannel: string;
  clusterId: string;
  /** Absent on tenants registered before multi-cluster support. */
  clusters?: { id: string; name: string; nodeGroups: string[] }[];
  nodeGroups: string[];
  namespaceQuota: number;
  pimEnabled: boolean;
  pimGroupId: string | null;
  pimStatus: string;
  tenantResourceQuota: {
    cpuRequest: number;
    cpuLimit: number;
    memoryRequest: number;
    memoryLimit: number;
  };
  argocdProject?: {
    repositories: string[];
    rootPath: string;
    helmRepositories: string[];
  };
};
